import { useQueries } from '@tanstack/react-query';
import { useFetchApi } from '../useFetchApi';
import { useNotification } from '../useNotification';
import { WebhookStats } from '../../shared/types';

export const calculateErrorRate = (
  successfulMessages: number,
  failedMessages: number
) => {
  if (successfulMessages === 0 && failedMessages === 0) {
    return 0;
  }
  return Math.round(
    (failedMessages / (successfulMessages + failedMessages)) * 100
  );
};

type WebhooksStats = {
  isLoading: boolean;
  isError: boolean;
  data: {
    errorRate: number;
  };
};

const WEBHOOK_STATS_STALE_TIME_IN_MS = 60 * 1000;

export const useWebhooksStats = (
  ids: string[]
): Record<string, WebhooksStats> => {
  const fetch = useFetchApi();
  const { showErrorWithResponse } = useNotification();

  const stats = useQueries({
    queries: ids.map((id) => ({
      // to avoid fetching stats for each webhook on every page load/view
      staleTime: WEBHOOK_STATS_STALE_TIME_IN_MS,
      queryKey: ['webhook-stats', id],
      queryFn: async () => {
        const data = (await fetch(`webhooks/${id}/stats`)) as WebhookStats;

        if (!data) {
          throw new Error('No data');
        }

        return {
          id,
          errorRate: calculateErrorRate(
            data.successfulMessages,
            data.failedMessages
          ),
        };
      },
      onError: showErrorWithResponse,
    })),
  });

  return stats.reduce<Record<string, WebhooksStats>>(
    (acc, { data, isLoading, isError }) => {
      if (data) {
        const { id, ...rest } = data;

        acc[id] = {
          isLoading,
          isError,
          data: rest,
        };
      }
      return acc;
    },
    {}
  );
};
